// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Configuracoes/WhatsAppTemplates.tsx
//  CRUD de templates de mensagem WhatsApp com preview de placeholders.
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Save, MessageSquare } from 'lucide-react';

import { Button }   from '@/components/ui/button';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch }   from '@/components/ui/switch';
import { Badge }    from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { useWhatsAppTemplates } from '@/features/whatsapp/hooks/useWhatsApp';
import type { WhatsAppTemplate } from '@/features/whatsapp/types';
import { getApiErrorMessage } from '@/lib/apiClient';

const PLACEHOLDER_REGEX = /\{(\w+)\}/g;

export default function WhatsAppTemplates() {
  const { query, criar, atualizar, excluir } = useWhatsAppTemplates();

  const [modalAberto,  setModalAberto]  = useState(false);
  const [editando,     setEditando]     = useState<WhatsAppTemplate | null>(null);
  const [chave,        setChave]        = useState('');
  const [titulo,       setTitulo]       = useState('');
  const [corpo,        setCorpo]        = useState('');
  const [isAtivo,      setIsAtivo]      = useState(true);
  const [paraExcluir,  setParaExcluir]  = useState<WhatsAppTemplate | null>(null);

  const placeholders = useMemo(() => {
    const matches = Array.from(corpo.matchAll(PLACEHOLDER_REGEX));
    return Array.from(new Set(matches.map(m => m[1])));
  }, [corpo]);

  const abrirNovo = () => {
    setEditando(null);
    setChave(''); setTitulo(''); setCorpo(''); setIsAtivo(true);
    setModalAberto(true);
  };

  const abrirEdicao = (t: WhatsAppTemplate) => {
    setEditando(t);
    setChave(t.chave); setTitulo(t.titulo); setCorpo(t.corpo); setIsAtivo(t.isAtivo);
    setModalAberto(true);
  };

  const handleSalvar = async () => {
    try {
      const req = { chave: chave.trim(), titulo: titulo.trim(), corpo, isAtivo };
      if (editando) await atualizar.mutateAsync({ id: editando.id, req });
      else          await criar.mutateAsync(req);
      toast.success(editando ? 'Template atualizado.' : 'Template criado.');
      setModalAberto(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const handleExcluir = async () => {
    if (!paraExcluir) return;
    try {
      await excluir.mutateAsync(paraExcluir.id);
      toast.success('Template excluído.');
      setParaExcluir(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const podeSalvar =
    chave.trim().length >= 2 &&
    titulo.trim().length >= 2 &&
    corpo.trim().length  >= 2;

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-6 w-6 text-green-600" />
          <div>
            <h1 className="text-2xl font-bold">Templates WhatsApp</h1>
            <p className="text-sm text-muted-foreground">
              Mensagens reutilizáveis com placeholders{' '}
              <code className="bg-muted px-1 rounded">{'{variavel}'}</code>.
            </p>
          </div>
        </div>
        <Button onClick={abrirNovo} className="gap-2">
          <Plus className="h-4 w-4" />
          Novo template
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Templates cadastrados</CardTitle>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-muted-foreground">Carregando...</p>
          ) : (query.data ?? []).length === 0 ? (
            <p className="text-muted-foreground">
              Nenhum template cadastrado. Clique em "Novo template" para começar.
            </p>
          ) : (
            <div className="space-y-3">
              {(query.data ?? []).map(t => {
                const phs = Array.from(new Set(Array.from(t.corpo.matchAll(PLACEHOLDER_REGEX)).map(m => m[1])));
                return (
                  <div key={t.id} className="border rounded p-3 flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{t.titulo}</span>
                        <code className="text-xs bg-muted px-1 rounded">{t.chave}</code>
                        {!t.isAtivo && <Badge variant="secondary">inativo</Badge>}
                      </div>
                      <p className="text-sm whitespace-pre-wrap text-muted-foreground line-clamp-3">
                        {t.corpo}
                      </p>
                      {phs.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {phs.map(p => (
                            <Badge key={p} variant="outline" className="font-mono text-xs">
                              {`{${p}}`}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => abrirEdicao(t)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setParaExcluir(t)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal criar/editar */}
      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar template' : 'Novo template'}</DialogTitle>
            <DialogDescription>
              Use <code className="bg-muted px-1 rounded">{'{nome}'}</code>,{' '}
              <code className="bg-muted px-1 rounded">{'{numero}'}</code>, etc. para campos variáveis.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Chave *</Label>
              <Input
                value={chave}
                onChange={(e) => setChave(e.target.value)}
                placeholder="dav-baixado"
                disabled={!!editando}
              />
              {editando && (
                <p className="text-xs text-muted-foreground">Chave não pode ser alterada.</p>
              )}
            </div>

            <div className="space-y-1">
              <Label>Título *</Label>
              <Input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="DAV baixado"
              />
            </div>

            <div className="md:col-span-2 space-y-1">
              <Label>Corpo *</Label>
              <Textarea
                value={corpo}
                onChange={(e) => setCorpo(e.target.value)}
                rows={6}
                placeholder="Olá {nome}, seu pedido nº {numero} está pronto."
              />
              {placeholders.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  <span className="text-xs text-muted-foreground mr-1">Variáveis:</span>
                  {placeholders.map(p => (
                    <Badge key={p} variant="outline" className="font-mono text-xs">
                      {`{${p}}`}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="md:col-span-2 flex items-center justify-between">
              <Label>Ativo</Label>
              <Switch checked={isAtivo} onCheckedChange={setIsAtivo} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAberto(false)}>Cancelar</Button>
            <Button
              onClick={handleSalvar}
              disabled={!podeSalvar || criar.isPending || atualizar.isPending}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {editando ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação exclusão */}
      <AlertDialog open={!!paraExcluir} onOpenChange={(o) => { if (!o) setParaExcluir(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir template?</AlertDialogTitle>
            <AlertDialogDescription>
              Confirma excluir o template <strong>{paraExcluir?.titulo}</strong>?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluir} className="bg-destructive hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
